/**
 * 채팅 백엔드 API 서비스
 *
 * 백엔드 스키마:
 * - ChatSession: 채팅 세션 (id, userId, title, lastReadMessageId, timestamps)
 * - ChatMessage: 채팅 메시지 (id, sessionId, role, content, contextChips, model, tokenCount, createdAt)
 *
 * 역할:
 * - 프론트엔드에서 Gemini API 호출 후 결과를 백엔드에 저장
 * - 세션 목록 조회, 메시지 불러오기
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * 토큰 가져오기 (localStorage에서)
 */
const getAuthToken = () => {
  const token = localStorage.getItem('accessToken');
  return token ? `Bearer ${token}` : null;
};

/**
 * API 요청 헬퍼
 */
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();

  if (!token) {
    console.warn('[ChatAPI] 인증 토큰이 없습니다. 저장을 건너뜁니다.');
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        ...options.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      // 백엔드 API가 아직 없으면 조용히 실패
      if (response.status === 404) {
        console.warn('[ChatAPI] API 엔드포인트가 아직 구현되지 않았습니다.');
        return null;
      }
      throw new Error(`API 오류: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.warn('[ChatAPI] 요청 실패:', error.message);
    return null;
  }
};

// ============================================
// 세션 (ChatSession) API
// ============================================

/**
 * 새 채팅 세션 생성
 * @param {string} title - 세션 제목 (선택)
 * @returns {Promise<{id: string, title: string, createdAt: string} | null>}
 */
export const createSession = async (title = null) => {
  return apiRequest('/api/chat/sessions', {
    method: 'POST',
    body: JSON.stringify({ title }),
  });
};

/**
 * 세션 목록 조회
 * @param {number} limit - 조회할 세션 수
 * @returns {Promise<Array | null>}
 */
export const getSessions = async (limit = 20) => {
  return apiRequest(`/api/chat/sessions?limit=${limit}`);
};

/**
 * 특정 세션 조회
 * @param {string} sessionId
 * @returns {Promise<Object | null>}
 */
export const getSession = async (sessionId) => {
  return apiRequest(`/api/chat/sessions/${sessionId}`);
};

/**
 * 세션 업데이트 (제목 변경 등)
 * @param {string} sessionId
 * @param {Object} updates - { title?, lastReadMessageId? }
 * @returns {Promise<Object | null>}
 */
export const updateSession = async (sessionId, updates) => {
  return apiRequest(`/api/chat/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
};

/**
 * 세션 삭제 (soft delete - archivedAt 설정)
 * @param {string} sessionId
 * @returns {Promise<Object | null>}
 */
export const deleteSession = async (sessionId) => {
  return apiRequest(`/api/chat/sessions/${sessionId}`, {
    method: 'DELETE',
  });
};

// ============================================
// 메시지 (ChatMessage) API
// ============================================

/**
 * 메시지 저장
 * @param {string} sessionId - 세션 ID
 * @param {Object} message - 메시지 데이터
 * @param {string} message.role - "user" | "assistant"
 * @param {string} message.content - 메시지 내용
 * @param {string} message.model - 사용된 모델 (assistant만)
 * @param {Object} message.contextChips - 컨텍스트 칩 데이터 (선택)
 * @returns {Promise<Object | null>}
 */
export const saveMessage = async (sessionId, message) => {
  return apiRequest(`/api/chat/sessions/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({
      role: message.role,
      content: message.content,
      model: message.model || null,
      contextChips: message.contextChips || null,
    }),
  });
};

/**
 * 세션의 메시지 목록 조회
 * @param {string} sessionId
 * @param {number} limit - 조회할 메시지 수
 * @param {string} before - 이 메시지 ID 이전의 메시지 조회 (페이지네이션)
 * @returns {Promise<Array | null>}
 */
export const getMessages = async (sessionId, limit = 50, before = null) => {
  let url = `/api/chat/sessions/${sessionId}/messages?limit=${limit}`;
  if (before) {
    url += `&before=${before}`;
  }
  return apiRequest(url);
};

/**
 * 메시지 삭제
 * @param {string} sessionId
 * @param {string} messageId
 * @returns {Promise<Object | null>}
 */
export const deleteMessage = async (sessionId, messageId) => {
  return apiRequest(`/api/chat/sessions/${sessionId}/messages/${messageId}`, {
    method: 'DELETE',
  });
};

// ============================================
// 편의 함수
// ============================================

/**
 * 대화 저장 (사용자 메시지 + AI 응답 한 번에)
 * 프론트에서 Gemini 호출 후 결과를 백엔드에 저장할 때 사용
 *
 * @param {string} sessionId - 세션 ID
 * @param {Object} userMessage - 사용자 메시지
 * @param {Object} assistantMessage - AI 응답 메시지
 * @returns {Promise<{userResult: Object, assistantResult: Object} | null>}
 */
export const saveConversation = async (sessionId, userMessage, assistantMessage) => {
  try {
    // 사용자 메시지 저장
    const userResult = await saveMessage(sessionId, {
      role: 'user',
      content: userMessage.content,
    });

    // AI 응답 저장
    const assistantResult = await saveMessage(sessionId, {
      role: 'assistant',
      content: assistantMessage.content,
      model: assistantMessage.model,
    });

    return { userResult, assistantResult };
  } catch (error) {
    console.warn('[ChatAPI] 대화 저장 실패:', error.message);
    return null;
  }
};

/**
 * 세션 생성 또는 조회
 * 기존 세션이 있으면 조회, 없으면 생성
 *
 * @param {string} sessionId - 프론트엔드에서 생성한 세션 ID (참조용)
 * @param {string} title - 세션 제목
 * @returns {Promise<Object | null>}
 */
export const getOrCreateSession = async (sessionId, title = null) => {
  // 먼저 기존 세션 조회 시도
  const existing = await getSession(sessionId);
  if (existing) {
    return existing;
  }

  // 없으면 새로 생성
  return createSession(title);
};

export default {
  // 세션
  createSession,
  getSessions,
  getSession,
  updateSession,
  deleteSession,

  // 메시지
  saveMessage,
  getMessages,
  deleteMessage,

  // 편의 함수
  saveConversation,
  getOrCreateSession,
};
